'use client';

/**
 * Site Layout Wrapper
 *
 * Client-side wrapper that handles sidebar state for marketing pages.
 * Exactly matches the original Edem AI landing page layout.
 *
 * Auth chrome resolves CLIENT-side via useUser(): the marketing tree renders
 * statically for CDN caching, so the server cannot know the visitor. The
 * session is decoded from local cookies (no network round trip), so the
 * logged-in state appears within a frame of hydration.
 */
import { type CSSProperties, useState } from 'react';

import { useUser } from '@kit/supabase/hooks/use-user';

import { ExploreFilterProvider } from '~/components/navigation/explore-filter-context';
import { MainNavbar } from '~/components/navigation/main-navbar';
import { MobileBottomNav } from '~/components/navigation/mobile-bottom-nav';
import { Sidebar } from '~/components/navigation/sidebar';
import {
  SidebarProvider,
  useSidebar,
} from '~/components/navigation/sidebar-context';
import { PROMO_BANNER_HEIGHT_PX } from '~/components/promo/promo-banner-constants';
import { ScrollGlow } from '~/components/scroll-glow';

import { SiteFooter } from './site-footer';

interface SiteLayoutWrapperProps {
  children: React.ReactNode;
}

function SiteLayoutContent({ children }: SiteLayoutWrapperProps) {
  const { data: user } = useUser();
  const { sidebarWidth, isAnimating } = useSidebar();
  const [promoBannerVisible, setPromoBannerVisible] = useState(false);

  return (
    <div
      className="relative min-h-screen overflow-x-clip"
      style={
        {
          background:
            'linear-gradient(to bottom right, #0a0a0a, #160a2a, #0d001a)',
          // WELCOME10 marquee height — the fixed navbar, sidebar and content
          // paddings all key off this variable so the banner pushes the
          // whole chrome down while it's visible.
          '--promo-banner-h': promoBannerVisible
            ? `${PROMO_BANNER_HEIGHT_PX}px`
            : '0px',
        } as CSSProperties
      }
    >
      {/* Ambient purple glow that eases with scroll — same as the main page */}
      <ScrollGlow />

      {/* WELCOME10 promo marquee — pinned above the navbar */}

      {/* Full-width top navbar — spans the whole top of the page */}
      <MainNavbar
        user={user ?? null}
        showCoinBalance={!!user}
        showStartChat={false}
      />

      {/* Sidebar — fixed, sits BELOW the navbar */}
      <Sidebar />

      {/* Main content — right of the sidebar and below the navbar */}
      <div
        className={`relative z-10 ml-0 flex min-h-screen max-w-full flex-col overflow-x-clip lg:ml-[var(--sb-w)] ${isAnimating ? 'transition-all duration-300 ease-in-out' : ''}`}
        style={
          {
            // Content sits right of the sidebar on desktop only (lg+); on mobile
            // the sidebar is an overlay, so the content keeps a 0 margin.
            '--sb-w': `${sidebarWidth}px`,
            // Clears the fixed 64px navbar plus the promo banner (if shown)
            paddingTop: 'calc(64px + var(--promo-banner-h, 0px))',
          } as CSSProperties
        }
      >
        {/* Page Content */}
        <main className="flex-1 overflow-x-clip pb-16 lg:pb-0">{children}</main>

        {/* Footer */}
        <SiteFooter />
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}

export function SiteLayoutWrapper(props: SiteLayoutWrapperProps) {
  return (
    <SidebarProvider>
      <ExploreFilterProvider>
        <SiteLayoutContent {...props} />
      </ExploreFilterProvider>
    </SidebarProvider>
  );
}
